import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

export default function RefundPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-20" data-testid="refund-policy-page">
      <SEO
        title="Refund Policy — All Sales Final"
        description="ProSource Labs refund policy. All research peptide sales are final due to cold-chain custody. Reshipments authorized only for verified fulfillment errors or shipping damage reported within 48 hours."
        path="/refund-policy"
      />
      <div className="overline">POLICY · CANCELLATIONS, RETURNS & REFUNDS</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-light leading-tight mt-3">
        All sales are final.
      </h1>
      <p className="text-xs font-mono-tech text-[#5C657A] mt-3 uppercase tracking-[0.2em]">
        Last updated: Feb 2026 · ProSource Labs LLC
      </p>

      <div className="mt-12 space-y-10 text-[#8A94A6] leading-relaxed">
        <section>
          <h2 className="font-heading text-xl text-white mb-3">The Short Version</h2>
          <p>
            Because of the sensitive nature of research-grade peptides and the regulations
            governing them, every order placed with <span className="text-white">ProSource Labs LLC</span> is
            considered final the moment it is submitted. The full terms below explain exactly what
            that means — and the narrow circumstances under which we will issue a reshipment.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Order Cancellations & Modifications</h2>
          <p>
            Orders cannot be cancelled, altered, or substituted once they have been placed. This
            includes changes to product, dosage, quantity, shipping address, or payment method. We
            recommend reviewing your cart carefully before checkout — and reaching out to support
            <em> before </em>placing your order if you have any questions.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Returns</h2>
          <p>
            <span className="text-white">Returns are not accepted</span> under any circumstances. Once
            a parcel leaves our facility, the cold-chain custody is broken and the contents can no
            longer be re-introduced to our inventory.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Refunds</h2>
          <p className="mb-3">
            Refunds will <span className="text-white">not</span> be issued for any of the following:
          </p>
          <ul className="list-disc list-outside ml-6 space-y-2">
            <li>Change of mind or buyer's remorse</li>
            <li>User error at checkout (wrong product, wrong dosage, wrong address)</li>
            <li>Refused, undeliverable, or unclaimed shipments</li>
            <li>Delays caused by the carrier once the parcel has left our facility</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Chargebacks</h2>
          <p>
            Any chargeback filed against a delivered or carrier-confirmed shipment will be formally
            disputed using full supporting documentation — including order records, tracking history,
            proof of delivery, and customer correspondence. Fraudulent chargebacks may be referred to
            collections and reported to the appropriate networks.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Reshipments</h2>
          <p className="mb-3">
            A complimentary reshipment may be authorized — at the sole discretion of ProSource Labs
            LLC — in the following verified cases:
          </p>
          <ul className="list-disc list-outside ml-6 space-y-2">
            <li>
              <span className="text-white">Fulfillment error</span> on our end (e.g. the wrong
              compound or dosage was shipped), confirmed against our outbound lot records.
            </li>
            <li>
              <span className="text-white">Shipping damage</span> resulting in a broken vial or
              compromised packaging, documented with clear photographs taken within
              <span className="text-white"> 48 hours of delivery</span>.
            </li>
          </ul>
          <p className="mt-3">
            Reshipment requests outside of these two categories will not be honored.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">How to Submit a Claim</h2>
          <p>
            If you believe you qualify for a reshipment under the criteria above, reach out through
            our <Link to="/contact" className="text-[#00C3FF] hover:text-white">Contact form</Link> or
            email <span className="text-[#00C3FF]">support@prosourcelabs.org</span> within
            <span className="text-white"> 48 hours of delivery</span>. Please include:
          </p>
          <ul className="list-disc list-outside ml-6 space-y-2 mt-3">
            <li>Your order number (e.g. <span className="font-mono-tech text-white">PSL-XXXXXXXX</span>)</li>
            <li>The email address used at checkout</li>
            <li>Clear, well-lit photographs of the issue (and packaging, if applicable)</li>
            <li>A brief description of the problem</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-xl text-white mb-3">Our Commitment</h2>
          <p>
            ProSource Labs LLC is committed to accurate fulfillment, sterile handling, and the
            timely dispatch of every order. The policies above exist to keep that promise fair to
            every researcher we serve, to prevent abuse of our reshipment goodwill, and to comply
            with the regulatory framework that governs research compounds. By placing an order with
            ProSource Labs LLC, you acknowledge that you have read these terms and agree to be bound
            by them in full.
          </p>
        </section>
      </div>
    </div>
  );
}
